// Layout of the invoice PDF in the sevDesk RE-1075 style. All values are pt; y values are measured from the
// page's TOP edge (the renderer converts to pdf-lib's bottom-up coordinates). The values are measured from
// sevDesk's own render of the synthetic invoice (tests/fixtures/golden/sevdesk-synthetic.pdf): text positions and
// sizes with `pdftotext -bbox`, fills, rules, fold marks and the wordmark from a 288 dpi raster. A text box's top is
// its line box (CSS-like: the glyphs' content area centred in it), so `baseline(top, size, line)` places the glyphs
// where sevDesk's PDFreactor does. tests/invoicing-fidelity.test.ts compares the page images within a tolerance.

export const PAGE_WIDTH = 595.28;
export const PAGE_HEIGHT = 841.89;
export const TEXT_COLOR = "#1a1a1a";
export const FILL_COLOR = "#ebebeb";

/** Open Sans vertical metrics (hhea, units per em 2048): text tops are converted to baselines with these. */
export const FONT_ASCENT = 2189 / 2048;
export const FONT_DESCENT = 600 / 2048;

/** Fold marks (105 mm and 210 mm) and the punch mark (148.5 mm) on the left edge, every page. */
export const FOLD_MARKS = [
  { y: 302.88, length: 10.75 },
  { y: 426.13, length: 19.25 },
  { y: 600.38, length: 10.75 },
] as const;
export const FOLD_MARK_COLOR = "#333333";
export const FOLD_MARK_THICKNESS = 1;

/** „boehler.dev“, Open Sans Bold, every page. */
export const WORDMARK = { size: 15, right: 542.28, top: 61.5 } as const;

/** „Florian Böhler  -  Bildsteinerstraße 13  -  6858 Schwarzach“ above the recipient, page 1. */
export const SENDER_LINE = { size: 6, x: 59.53, top: 135.98, separator: "  -  " } as const;

/** Recipient block (name, street, „zip city“, country), page 1. */
export const RECIPIENT_BLOCK = { size: 9, line: 10.8, x: 59.53, top: 170.09 } as const;

/**
 * Meta block right of the recipient, page 1: the number row is larger, then the date rows, a gap, the customer rows.
 * A draft keeps the number row's space empty, as sevDesk does, so finalizing moves nothing.
 */
export const META = {
  x: 362.83, right: 545.66, top: 150.25,
  first: { size: 11, line: 15.46 },
  row: { size: 7, line: 10.67 },
  gap: 8.5,
} as const;

/** Document title („Rechnung Nr. RE-1086“), bold, page 1. */
export const TITLE = { size: 12, line: 16.34, x: 62.69, top: 262.96 } as const;

/** Intro and closing text: same size, line and paragraph gap; the width is the table's. */
export const BODY_TEXT = { size: 9, line: 10.8, paragraphGap: 9 } as const;
/** Intro starts this far below the title line. */
export const INTRO_GAP = 20.37;
/** Closing starts this far below the totals block. */
export const CLOSING_GAP = 22.67;

/** Position table. */
export const TABLE = {
  x: 62.73,
  width: 478.77,
  // Column widths; Beschreibung takes the rest.
  posWidth: 28.34,
  qtyWidth: 46.5,
  unitWidth: 76.53,
  totalWidth: 79.4,
  padX: 2.8,
  /**
   * Title and description wrap this far before the Beschreibung column's right edge (instead of padX). The wrap
   * width (234.4) is the middle of the range that reproduces every line break of RE-1075 and the synthetic invoice.
   */
  descPadRight: 10.8,
  size: 9,
  headerHeight: 17.01,
} as const;
/** Cell padding above the title line and below the last line (measured: sevDesk puts it all below). */
export const CELL_PAD_TOP = 0;
export const CELL_PAD_BOTTOM = 5.67;
/** Title lines (bold) and description lines. */
export const TITLE_LINE = 17.01;
export const DESC_LINE = 14.4;

/** Table top on page 1: at least this, else this gap below the intro. */
export const TABLE_TOP_FIRST = 368.25;
export const TABLE_GAP_AFTER_INTRO = 11.34;
/** Table (or totals, or a continued closing) top on later pages. */
export const TABLE_TOP_NEXT = 141.74;
/** Nothing of the body goes below this line; only the page number and the footer. */
export const BODY_BOTTOM = 740;

/**
 * Totals block (kept together): spacer, hairline, a gap, then the rows netto / Kleinunternehmer note / brutto; netto and
 * brutto are filled across the table, the note is not. Labels start in the Beschreibung column (`cellWidth` in).
 */
export const TOTALS = { spacer: 0, hairline: 0.75, gap: 1.62, rowHeight: 17.01, cellWidth: 28.34, filled: [true, false, true] } as const;
export const TOTALS_HEIGHT = TOTALS.spacer + TOTALS.hairline + TOTALS.gap + 3 * TOTALS.rowHeight;

/** „n/N“, every page. */
export const PAGE_NUMBER = { size: 7, right: 552.76, top: 749.93 } as const;

/** Four-column footer, every page. sevDesk sets the four-line address column tighter than the other three. */
export const FOOTER = {
  size: 7, top: 780.64,
  columns: [{ x: 56.69, line: 10 }, { x: 178.58, line: 11.5 }, { x: 300.47, line: 11.5 }, { x: 422.36, line: 11.5 }],
} as const;

/** Baseline of a text line whose box starts at `top`; without `lineHeight` the box is the font's content area. */
export function baseline(top: number, size: number, lineHeight?: number): number {
  const content = (FONT_ASCENT + FONT_DESCENT) * size;
  return top + ((lineHeight ?? content) - content) / 2 + FONT_ASCENT * size;
}

/**
 * EPC payment QR code: 3 × 3 cm including the 4-module quiet zone, floated at the right edge of the closing block
 * beside the payment terms; closing lines beside it wrap `gap` short of it. Caption („Zahlen mit Code“) underneath.
 */
export const EPC = { size: 85.04, quietZone: 4, gap: 12, captionSize: 7, captionGap: 2, caption: "Zahlen mit Code" } as const;
export const EPC_HEIGHT = EPC.size + EPC.captionGap + (FONT_ASCENT + FONT_DESCENT) * EPC.captionSize;
